import { keyframes as cnKeyframes } from '@emotion/css';
import styled from '@emotion/styled';
import { motion, useMotionValue } from 'framer-motion';
import { useLayoutEffect, useRef } from 'react';
import { useOverlayTrigger } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager.jsx';

import { useAuth } from '../../store/AuthStore';
import { webAppSettingsStore } from '../../store/web-app-settings/WebAppSettingsStore';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useIsRTL } from '../../hooks/useIsRTL';
import { useNotificationsById } from '../../store/notifications/useNotifications.ts';
import { useApi, useSelectedHomey } from '../../store/SelectedHomeyStore.hooks.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';

import { su, suPx } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { AnimationRemain } from '../../components/animation/AnimationRemain';
import { Glyph } from '../../components/common/Glyph';
import { Notifications } from '../../components/notifications/Notifications';
import { Overlay } from '../../components/overlay/Overlay';

import { GlobalSearch } from './global-search/GlobalSearch';
import { HeaderButton } from './HeaderButton';
import { UserMenu } from './UserMenu';

export function Header(props) {
  const { i18n } = useI18n();
  const isRTL = useIsRTL();

  const { cloudUser } = useAuth();
  const { api } = useApi();
  const selectedHomey = useSelectedHomey();
  const { userMe } = useUserMe();
  const notifications = useNotificationsById();

  function handleHomeySelect({ homeyId }) {
    RouteManager.toHomey(homeyId);
  }

  const animationMV = useMotionValue(null);

  useLayoutEffect(() => {
    if (props.isRightOpen) {
      const panelSlideInBidi = isRTL ? panelSlideInRTL : panelSlideIn;
      const panelSlideOutBidi = isRTL ? panelSlideOutRTL : panelSlideOut;
      animationMV.set(`${panelSlideInBidi} ease-in-out 750ms`);

      return function () {
        animationMV.set(`${panelSlideOutBidi} ease-in-out 500ms`);
      };
    }
  }, [props.isRightOpen, animationMV, isRTL]);

  const timelineTriggerRef = useRef();
  const timelineTriggerState = useOverlayTriggerState({ defaultOpen: false });
  const timelineOverlayTrigger = useOverlayTrigger(
    { type: 'listbox' },
    timelineTriggerState,
    timelineTriggerRef,
  );

  return (
    <Header.Root>
      <Header.Children>{props.children}</Header.Children>
      <Header.Content
        isRightOverlay={props.isRightOverlay}
        isRightOpen={props.isRightOpen}
        style={{
          '--animation': animationMV,
        }}
      >
        <>
          {props.isMinimal !== true && <GlobalSearch />}

          {selectedHomey && props.isMinimal !== true && (
            <Header.Item>
              <HeaderButton
                {...timelineOverlayTrigger.triggerProps}
                ref={timelineTriggerRef}
                title={i18n.messageFormatter('header.notifications')}
                background={theme.color.component}
                iconHoverColor
                onPress={timelineTriggerState.toggle}
              >
                <Glyph source={glyph.core.bellFill} size="26px" color={theme.color.icon_medium} />
              </HeaderButton>

              <AnimationRemain condition={timelineTriggerState.isOpen} delay={200}>
                {(animationRemainProps) => {
                  return (
                    <Overlay
                      targetRef={timelineTriggerRef}
                      overlayProps={timelineOverlayTrigger.overlayProps}
                      overlayTriggerState={timelineTriggerState}
                      offset={suPx(1)}
                      animationRemainProps={animationRemainProps}
                      autoFocus={true}
                      restoreFocus={true}
                    >
                      <Notifications
                        userMe={userMe}
                        notificationsById={notifications.byId}
                        onRequestDelete={({ id }) => {
                          api.notifications
                            .deleteNotification({ id })
                            .catch(ToastManager.handleError);
                        }}
                      />
                    </Overlay>
                  );
                }}
              </AnimationRemain>
            </Header.Item>
          )}

          <Header.Item>
            <HeaderButton
              title={i18n.messageFormatter('header.darkmode')}
              background={theme.color.component}
              onPress={() => {
                webAppSettingsStore.toggleDarkMode();
              }}
            >
              <Glyph source={glyph.core.moon} size="26px" color={theme.color.icon_medium} />
            </HeaderButton>
          </Header.Item>

          <Header.Item>
            <UserMenu
              cloudUser={cloudUser}
              selectedHomey={selectedHomey}
              onHomeySelect={handleHomeySelect}
            />
          </Header.Item>
        </>
      </Header.Content>
    </Header.Root>
  );
}

const panelSlideIn = cnKeyframes`
  0% {
    transform: translateX(420px);
  }

  60% {
    transform: translateX(-21px);
  }

  100% {
    transform: translateX(0);
  }
`;

const panelSlideInRTL = cnKeyframes`
  0% {
    transform: translateX(-420px);
  }

  60% {
    transform: translateX(21px);
  }

  100% {
    transform: translateX(0);
  }
`;

const panelSlideOut = cnKeyframes`
  0% {
    transform: translateX(-420px);
  }

  100% {
    transform: translateX(0);
  }
`;

const panelSlideOutRTL = cnKeyframes`
  0% {
    transform: translateX(420px);
  }

  100% {
    transform: translateX(0);
  }
`;

Header.Root = styled.header`
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: ${su(9)};
  padding-top: ${su(2)};
  padding-bottom: ${su(2)};
`;

Header.Content = styled(motion.nav)`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
  margin: 0 ${su(2)};
  animation: var(--animation, none);
  animation-fill-mode: forwards;
  z-index: ${theme.zIndex.header};

  ${(props) => props.isRightOverlay && props.isRightOpen && { paddingInlineEnd: 420 }}
`;

Header.Children = styled.div`
  z-index: ${theme.zIndex.header};
`;

Header.Item = styled.div`
  margin-inline-start: ${su(2)};

  &:first-of-type {
    margin-inline-start: 0;
  }
`;
